<template>
  <div class="prompt-viewer">
    <a-card :bordered="false">
      <template #title>
        <a-space>
          <span>提示词展示</span>
          <a-radio-group v-model:value="activeTab" size="small" button-style="solid">
            <a-radio-button value="highlight">高亮视图</a-radio-button>
            <a-radio-button value="code">代码视图</a-radio-button>
          </a-radio-group>
        </a-space>
      </template>

      <!-- 高亮视图：保留现有知识块彩色高亮和点击跳转 -->
      <pre v-show="activeTab === 'highlight'" ref="promptContainer" class="prompt-content"><code v-html="highlightedPrompt"></code></pre>

      <!-- 代码视图：CodeMirror 6 只读 XML 编辑器 -->
      <div v-show="activeTab === 'code'" ref="cmContainer" class="cm-container"></div>
    </a-card>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { EditorView } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { xml } from '@codemirror/lang-xml';
import { basicSetup } from 'codemirror';
import { KNOWLEDGE_COLORS, PLACEHOLDER_COLORS } from '@/constants/knowledgeColors';

export default {
  name: 'PromptViewer',
  props: {
    prompt: {
      type: String,
      default: ''
    },
    knowledgeMapping: {
      type: Array,
      default: () => []
    },
    selectedKnowledgeId: {
      type: Number,
      default: null
    },
    selectedPlaceholder: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const promptContainer = ref(null);
    const cmContainer = ref(null);
    const activeTab = ref('highlight');
    let editorView = null;

    // 代码视图使用无标记的干净 XML
    const cleanPrompt = computed(() => {
      if (!props.prompt) return '';
      // 1. 剥离 HTML 注释标记
      // 2. 逐行 trimEnd（消除注释留下的仅含空格的"假空行"）
      // 3. 压缩 3+ 连续空行为 1 行
      return props.prompt
        .replace(/<!--[\s\S]*?-->/g, '')
        .split('\n')
        .map(line => line.trimEnd())
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    });

    // 创建或更新 CodeMirror 实例
    function ensureEditorCreated() {
      nextTick(() => {
        if (!cmContainer.value) return;
        if (!editorView) {
          editorView = new EditorView({
            state: EditorState.create({
              doc: cleanPrompt.value,
              extensions: [
                basicSetup,
                xml(),
                EditorView.editable.of(false),
                EditorView.lineWrapping,
              ],
            }),
            parent: cmContainer.value,
          });
        } else {
          editorView.dispatch({
            changes: { from: 0, to: editorView.state.doc.length, insert: cleanPrompt.value },
          });
        }
      });
    }

    // 切换到代码视图时初始化编辑器
    watch(activeTab, (newTab) => {
      if (newTab === 'code') {
        ensureEditorCreated();
      }
    });

    // prompt 变化时同步代码视图内容
    watch(cleanPrompt, () => {
      if (activeTab.value === 'code' && editorView) {
        editorView.dispatch({
          changes: { from: 0, to: editorView.state.doc.length, insert: cleanPrompt.value },
        });
      }
    });

    onUnmounted(() => {
      if (editorView) {
        editorView.destroy();
        editorView = null;
      }
    });

    // 高亮提示词
    const highlightedPrompt = computed(() => {
      if (!props.prompt) return '';

      // 先转义所有HTML和XML标签，确保XML标签作为文本显示
      let highlighted = escapeHtml(props.prompt);

      // 知识内容类型到 XML 标签名的映射（与后端保持一致）
      const TYPE_TAG_MAP = {
        prompt: 'prompt_knowledge',
        dataflow: 'dataflow',
        pin_info: 'pin_info',
        case: 'case',
        principle: 'principle',
        synonymDataStreams: 'synonym_streams',
      };

      // 收集所有知识条目的插入位置（从后往前处理避免索引偏移）
      const insertions = [];
      props.knowledgeMapping.forEach((mapping) => {
        const tagName = TYPE_TAG_MAP[mapping.type] || 'knowledge_item';
        const color = KNOWLEDGE_COLORS[mapping.type] || '#f0f0f0';
        const knowledgeId = mapping.knowledge_id;

        // 在转义后文本中搜索开始标签（属性顺序：id 排第一）
        // escapeHtml 通过 div.textContent 实现，只转义 < > &，不转义 "，所以 " 保持原样
        const startToken = `&lt;${tagName} id="${knowledgeId}"`;
        const endToken = `&lt;/${tagName}&gt;`;

        const startIdx = highlighted.indexOf(startToken);
        if (startIdx === -1) return;

        const endTokenIdx = highlighted.indexOf(endToken, startIdx);
        if (endTokenIdx === -1) return;

        const endIdx = endTokenIdx + endToken.length;

        insertions.push({ startIdx, endIdx, knowledgeId, color });
      });

      // 从后往前插入，避免前面的插入影响后面的索引
      insertions.sort((a, b) => b.startIdx - a.startIdx);
      insertions.forEach(({ startIdx, endIdx, knowledgeId, color }) => {
        const openSpan = `<span class="knowledge-content" data-knowledge-id="${knowledgeId}" style="display: block; background-color: ${color};">`;
        const closeSpan = '</span>';
        highlighted = highlighted.substring(0, startIdx) + openSpan + highlighted.substring(startIdx, endIdx) + closeSpan + highlighted.substring(endIdx);
      });

      // 兜底：移除所有未被处理的 HTML 注释（转义后呈现为 &lt;!-- ... --&gt;）
      // 包括：模板原始注释、未匹配到 knowledgeMapping 的 KNOWLEDGE 标记残留等
      // 此步骤必须在 &amp; 恢复之前执行，避免注释内容中的 &amp; 提前被还原
      highlighted = highlighted.replace(/&lt;!--[\s\S]*?--&gt;/g, '');

      // 保持XML标签转义状态（&lt; 和 &gt;），这样XML标签会作为文本显示
      // 恢复被转义的 & 符号（&amp; -> &），以便内容中的 & 符号能正确显示
      // 注意：escapeHtml 会将 & 转义为 &amp;，我们需要恢复它
      // 由于 &lt; &gt; &quot; 等实体不会被匹配（它们不是 &amp;），所以直接替换是安全的
      highlighted = highlighted.replace(/&amp;/g, '&');

      return highlighted;
    });

    // 转义HTML
    const escapeHtml = (text) => {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    };

    // 转义正则表达式特殊字符
    const escapeRegex = (str) => {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    // 滚动到指定知识位置
    const scrollToKnowledge = (knowledgeId) => {
      nextTick(() => {
        if (!promptContainer.value) return;

        // 选择可见的 knowledge-content 元素，而不是隐藏的 knowledge-marker
        const knowledgeElement = promptContainer.value.querySelector(`.knowledge-content[data-knowledge-id="${knowledgeId}"]`);

        if (knowledgeElement) {
          knowledgeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // 添加高亮效果
          knowledgeElement.style.border = '2px solid #1890ff';
          setTimeout(() => {
            knowledgeElement.style.border = '';
          }, 2000);
        }
      });
    };

    // 滚动到指定占位符位置
    const scrollToPlaceholder = (placeholderName) => {
      if (!promptContainer.value) return false;

      // 占位符名称映射（用于处理占位符名称的映射关系）
      const placeholderNameMap = {
        // 下划线命名映射到驼峰命名
        'csv_data_stream': 'collectedDataStreams',
        'csv_content': 'collectedDataStreams',
        'fault_code_list': 'engineFaultCodes',
        'working_conditions': 'workingConditions',
        'selected_streams': 'selectedStreams',
        // 驼峰命名保持原样
        'collectedDataStreams': 'collectedDataStreams',
        'engineFaultCodes': 'engineFaultCodes',
        'workingConditions': 'workingConditions',
        'selectedStreams': 'selectedStreams'
      };

      // 尝试匹配的占位符名称列表
      const possibleNames = [placeholderName];
      if (placeholderNameMap[placeholderName]) {
        possibleNames.push(placeholderNameMap[placeholderName]);
      }
      // 添加反向映射（从驼峰到下划线）
      const reverseMap = {
        'collectedDataStreams': ['csv_data_stream', 'csv_content'],
        'engineFaultCodes': ['fault_code_list'],
        'workingConditions': ['working_conditions'],
        'selectedStreams': ['selected_streams']
      };
      if (reverseMap[placeholderName]) {
        possibleNames.push(...reverseMap[placeholderName]);
      }

      // 依次尝试匹配
      for (const name of possibleNames) {
        // 选择可见的 placeholder-content 元素
        const placeholderElement = promptContainer.value.querySelector(`.placeholder-content[data-placeholder="${name}"]`);

        if (placeholderElement) {
          nextTick(() => {
            placeholderElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // 添加高亮效果
            placeholderElement.style.border = '2px solid #52c41a';
            placeholderElement.style.boxShadow = '0 0 8px rgba(82, 196, 26, 0.5)';
            setTimeout(() => {
              placeholderElement.style.border = '';
              placeholderElement.style.boxShadow = '';
            }, 2000);
          });
          return true;
        }
      }

      return false;
    };

    // 监听selectedKnowledgeId变化
    watch(() => props.selectedKnowledgeId, (knowledgeId) => {
      if (knowledgeId) {
        scrollToKnowledge(knowledgeId);
      }
    });

    // 监听selectedPlaceholder变化
    watch(() => props.selectedPlaceholder, (placeholder) => {
      if (placeholder) {
        scrollToPlaceholder(placeholder);
      }
    });

    return {
      promptContainer,
      cmContainer,
      activeTab,
      highlightedPrompt,
      scrollToKnowledge,
      scrollToPlaceholder
    };
  }
};
</script>

<style scoped>
.prompt-viewer {
  margin-bottom: 16px;
}

.prompt-content {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre;
  word-wrap: normal;
  overflow-x: auto;
  overflow-y: auto;
  max-height: 600px;
  margin: 0;
}

.prompt-content code {
  display: block;
  white-space: pre;
  word-wrap: normal;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
}

.cm-container {
  max-height: 600px;
  overflow: auto;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 12px;
}

/* CodeMirror 基础样式覆盖 */
.cm-container :deep(.cm-editor) {
  height: 100%;
  max-height: 600px;
}

.cm-container :deep(.cm-scroller) {
  font-family: 'Courier New', monospace;
  font-size: 12px;
  line-height: 1.6;
}

.knowledge-content {
  display: block;
  padding: 8px;
  margin: 4px 0;
  border-radius: 4px;
  transition: all 0.3s;
}

.knowledge-content:hover {
  opacity: 0.8;
}

.placeholder-content {
  display: block;
  padding: 8px;
  margin: 4px 0;
  border-radius: 4px;
  transition: all 0.3s;
}

.placeholder-content:hover {
  opacity: 0.85;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
